import { cloneDeep } from "es-toolkit/object";
import { getPath } from "src/basepath.ts";
import { adaptTimeWindowBodyParams } from "src/components/features/medium/AssetsFilter/components/TimeWindow/TimeWindow.resolve";
import { getUserTzContextFromStores } from "src/components/features/medium/AssetsFilter/components/TimeWindow/TimeWindow.tzStore";
import { accessTokenAtom, refreshTokenAtom } from "src/utils/auth/auth-atoms";
import { authService } from "src/utils/auth/auth-service";
import { AppStore } from "src/utils/jotai";
import { notifyFetchError, notifyNetworkError } from "@utils/notification.ts";
import { queryClient } from "@utils/query-client.ts";

const adaptedTimeWindowBodyParams = (body: Record<string, any> = {}) =>
  adaptTimeWindowBodyParams(body, getUserTzContextFromStores());

function defaultFetchOptions(body: any, method: string) {
  const headers = {
    headers: {
      "Content-Type": "application/json",
      refresh_token: AppStore.get(refreshTokenAtom),
      Authorization: `Bearer ${AppStore.get(accessTokenAtom)}`,
    },
  };
  if (!body) return { ...headers, method };
  else {
    let cleanBody = body;
    if (!Array.isArray(body)) {
      cleanBody = { ...body };
      Object.keys(cleanBody).forEach(
        (key) => cleanBody[key] === undefined && delete cleanBody[key]
      );
    }
    return Object.assign({}, headers, {
      method,
      body: JSON.stringify(cleanBody),
    });
  }
}

class PromiseWrapper<T> extends Promise<T> {
  constructor(...args: ConstructorParameters<typeof Promise<T>>) {
    super(...args);
  }

  async invalidate(
    ...invalidateQueryParams: Parameters<typeof queryClient.invalidateQueries>
  ): Promise<T> {
    const resp = await this;
    if (resp && (resp as any).ok) {
      // invalidateQueries (in v5) accepts only an object
      void queryClient.invalidateQueries(
        typeof invalidateQueryParams[0] === "object"
          ? invalidateQueryParams[0]
          : {
              queryKey: invalidateQueryParams[0],
              ...(invalidateQueryParams[1] || {}),
            }
      );
    }

    return resp;
  }
}

function wrapPromise<T>(promise: Promise<T>) {
  return new PromiseWrapper<T>((resolve, reject) => {
    void promise.then(resolve);
    void promise.catch(reject);
  });
}

export type FetchMethod = "GET" | "PUT" | "DELETE" | "POST" | "PATCH";

type FetchError = { error: any; isError: true };

const isFetchError = (r: Response | FetchError): r is FetchError =>
  !!(r as any).isError;

// used to accumlate all routes we get for debugging purposes (primarily for comparing with the routes in stress tests)
const debugRoutesList = new Set<string>();
(window as any)._debugRoutesList = debugRoutesList;
const DEBUG_ROUTES_LIST = false;

export function dfetch(
  url: string,
  body?: any,
  method: "GET" | "PUT" | "DELETE" | "POST" | "PATCH" = "GET",
  options: {
    propagateError?: boolean;
    preventNotification?: boolean;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  } = {}
) {
  // convert `timeWindow` to `start_time` / `end_time` (what the backend expects),
  // then remove keys with empty value and sort keys alphabetically
  body = getCleanBodyParams(adaptedTimeWindowBodyParams(body));

  const promise = authService.refreshAccessTokenIfExpired().then(() => {
    let fetchOptions = defaultFetchOptions(body, method);

    url = url.replace(/\/$/, "");

    const hasBodyParams = body && Object.keys(body).length > 0;

    if (["GET", "DELETE"].includes(method)) {
      if (hasBodyParams) {
        url = url + "?" + new URLSearchParams(body);
      }
      fetchOptions = defaultFetchOptions(null, method);
    }

    // Add custom headers if provided (must be done AFTER fetchOptions is finalized)
    if (options.headers) {
      fetchOptions = {
        ...fetchOptions,
        headers: {
          ...fetchOptions.headers,
          ...options.headers,
        },
      };
    }

    const normalizedUrl = getPath(url);

    if (DEBUG_ROUTES_LIST) {
      debugRoutesList.add(normalizedUrl);
    }

    return fetch(normalizedUrl, {
      ...(fetchOptions as RequestInit),
      signal: options.signal,
    })
      .catch((error) => {
        if (error?.name === "AbortError") {
          throw error;
        }
        return { error, isError: true } as FetchError;
      })
      .then(async (response: Response | FetchError) => {
        if (isFetchError(response)) {
          notifyFetchError(
            response.error?.message ||
              `Unknown error: "${JSON.stringify(response.error)}"`
          );

          return Promise.reject(response.error);
        }

        if (!response.ok) {
          const resJson = await response.json();
          // generic error:
          const error = new Error(resJson.message);

          notifyNetworkError(response);

          // When the tenant's license was revoked, every /api call 401s with
          // this specific message. Bounce to login so the user is locked out on
          // next refresh. Scoped to this message so we don't hijack the benign
          // "Could not validate credentials" 401s the app handles during auth
          // bootstrap.
          if (
            response.status === 401 &&
            resJson?.message === "This tenant has been disabled" &&
            !window.location.pathname.endsWith("/login")
          ) {
            void authService.logout();
          }

          if (options.propagateError) {
            return Promise.reject(resJson);
          }

          // Reject the promise instead of throwing
          return Promise.reject(error);
        }

        return response;
      });
  });

  return wrapPromise(promise);
}

export type FetchParams = {
  url?: string;
  body?: any;
  headers?: Record<string, string>;
  caller?: string;
  signal?: AbortSignal;
};

export async function dfetchFromParams<T>(params: FetchParams) {
  if (!params.url) throw new Error("URL is required");
  return await dfetch(params.url, params.body, "GET", {
    headers: params.headers,
    signal: params.signal,
  }).then((res) => {
    if (!res.ok) {
      throw res;
    }
    return res.json?.() as T;
  });
}

export const getCleanBodyParams = (bodyParams?: Record<string, unknown>) => {
  if (!bodyParams) {
    return undefined;
  }
  // deep-cloning the `body` assures it should not be mutated anywhere else in the code flow,
  // because any such mutation will not be actually sent to the backend.
  const body = cloneDeep(bodyParams);

  // special cases (arrays which if are empty, remove from the body params)
  const specialCases = ["appTags", "appOwners"];

  return body
    ? Object.fromEntries(
        Object.entries(body)
          .filter(([key, value]) => {
            if (specialCases.includes(key) && Array.isArray(value)) {
              if (value.length === 0) {
                return false;
              }
            }
            return value !== undefined && value !== "";
          })
          .sort(([key1], [key2]) => key1.localeCompare(key2))
      )
    : {};
};

export const getStringifiedBodyParams = (bodyParams?: Record<string, unknown>) =>
  JSON.stringify(getCleanBodyParams(bodyParams));
